import {
    PayloadAction,
    createAsyncThunk,
    createEntityAdapter,
    createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import * as biometricUserService from '../../service/biometric-user.service';
import {
    BiometricDeviceUser,
    BiometricDeviceUserRequest,
    BiometricDeviceUserSearchCriteria,
} from '../../service/model/biometric.model';
import { PaginatedResponse } from '../../service/model/paginated-response';
import { showNotification } from '../alerts/alert.slice';
import { NotificationType } from '../../shared/enums';
import { getAxiosErrorMessage } from '../../service/axios/axios.call.service';

export interface BiometricUserState {
    isLoading: boolean;
    currentPage: number;
    totalPages: number;
    totalRecords: number;
}

const biometricUsersAdapter = createEntityAdapter<BiometricDeviceUser>({
    sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export const initialState = biometricUsersAdapter.getInitialState({
    isLoading: false,
    currentPage: 0,
    totalPages: 0,
    totalRecords: 0,
} as BiometricUserState);

export const fetchBiometricDeviceUsersPage = createAsyncThunk<
    PaginatedResponse<BiometricDeviceUser>,
    BiometricDeviceUserSearchCriteria
>('biometricUsers/page', async (search, { rejectWithValue }) => {
    return biometricUserService
        .getBiometricDeviceUserPage(search)
        .catch((errors) => {
            return rejectWithValue(errors);
        });
});

export const createBiometricDeviceUser = createAsyncThunk<
    BiometricDeviceUser,
    BiometricDeviceUserRequest
>('biometricUsers/create', async (request, { rejectWithValue, dispatch }) => {
    return biometricUserService
        .createBiometricDeviceUser(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Biometric user created successfully',
                    type: NotificationType.Success,
                }),
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                }),
            );
            return rejectWithValue(errors);
        });
});

export const updateBiometricDeviceUser = createAsyncThunk<
    BiometricDeviceUser,
    BiometricDeviceUserRequest
>('biometricUsers/update', async (request, { rejectWithValue, dispatch }) => {
    return biometricUserService
        .updateBiometricDeviceUser(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Biometric user updated successfully',
                    type: NotificationType.Success,
                }),
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                }),
            );
            return rejectWithValue(errors);
        });
});

export const deleteBiometricDeviceUser = createAsyncThunk<number, number>(
    'biometricUsers/delete',
    async (id, { rejectWithValue, dispatch }) => {
        return biometricUserService
            .deleteBiometricDeviceUser(id)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Biometric user deleted successfully',
                        type: NotificationType.Success,
                    }),
                );
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    }),
                );
                return rejectWithValue(errors);
            });
    },
);

const biometricUserSlice = createSlice({
    name: 'biometricUsers',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchBiometricDeviceUsersPage.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchBiometricDeviceUsersPage.fulfilled,
                (
                    state,
                    action: PayloadAction<
                        PaginatedResponse<BiometricDeviceUser>
                    >,
                ) => {
                    biometricUsersAdapter.setAll(state, action.payload.data);
                    state.currentPage = action.payload.currentPage;
                    state.totalPages = action.payload.totalPages;
                    state.totalRecords = action.payload.totalRecords;
                    state.isLoading = false;
                },
            )
            .addCase(fetchBiometricDeviceUsersPage.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(createBiometricDeviceUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                createBiometricDeviceUser.fulfilled,
                (state, action: PayloadAction<BiometricDeviceUser>) => {
                    biometricUsersAdapter.addOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(createBiometricDeviceUser.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateBiometricDeviceUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                updateBiometricDeviceUser.fulfilled,
                (state, action: PayloadAction<BiometricDeviceUser>) => {
                    biometricUsersAdapter.upsertOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(updateBiometricDeviceUser.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteBiometricDeviceUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                deleteBiometricDeviceUser.fulfilled,
                (state, action: PayloadAction<number>) => {
                    biometricUsersAdapter.removeOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(deleteBiometricDeviceUser.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const {
    selectAll: selectBiometricUsers,
    selectById: selectBiometricUserById,
} = biometricUsersAdapter.getSelectors<RootState>(
    (state) => state.biometricUsers,
);

export const selectIsLoading = (state: RootState) =>
    state.biometricUsers?.isLoading;
export const selectCurrentPage = (state: RootState) =>
    state.biometricUsers.currentPage;
export const selectTotalPages = (state: RootState) =>
    state.biometricUsers.totalPages;
export const selectTotalRecords = (state: RootState) =>
    state.biometricUsers.totalRecords;

export const biometricUserActions = biometricUserSlice.actions;

export default biometricUserSlice.reducer;
